import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, Scale, Lock, ChevronDown, ArrowUpRight } from "lucide-react";
import { useStore } from "../context/StoreContext";

const SECTIONS = [
  { id: "livraison", key: "shipping", Icon: Truck, links: [{ slug: "cgv", fr: "Conditions générales de vente", en: "Terms of sale" }] },
  { id: "mentions-legales", key: "legal", Icon: Scale, links: [{ slug: "mentions-legales", fr: "Mentions légales complètes", en: "Full legal notice" }, { slug: "cgu", fr: "Conditions d'utilisation", en: "Terms of use" }] },
  { id: "confidentialite", key: "privacy", Icon: Lock, links: [{ slug: "confidentialite", fr: "Politique de confidentialité", en: "Privacy policy" }, { slug: "cookies", fr: "Cookies", en: "Cookies" }, { slug: "rgpd", fr: "RGPD & nLPD", en: "GDPR & nFADP" }] },
];

const Block = ({ id, sec, Icon, links, lang, open, onToggle, testId }) => (
  <motion.section
    id={id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
    className="scroll-mt-32 rounded-[1.25rem] border border-stone-200/80 bg-white/70 backdrop-blur-sm overflow-hidden"
    data-testid={testId}
  >
    <button
      type="button" onClick={onToggle} aria-expanded={open}
      className="w-full flex items-start gap-4 text-left px-6 lg:px-8 py-6 hover:bg-stone-50/60 transition-colors"
      data-testid={`${testId}-toggle`}
    >
      <span className="mt-0.5 w-9 h-9 shrink-0 rounded-full bg-[#C85A32]/10 text-[#C85A32] flex items-center justify-center"><Icon size={16} /></span>
      <span className="flex-1">
        <span className="block font-serif text-xl lg:text-2xl text-stone-900">{sec.h}</span>
        <span className="block mt-1 text-sm text-stone-500 line-clamp-1">{sec.p[0]}</span>
      </span>
      <ChevronDown size={18} className={`mt-1 shrink-0 text-stone-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
    <div className={`grid transition-[grid-template-rows,opacity] duration-500 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
      <div className="overflow-hidden">
        <div className="px-6 lg:px-8 pb-7 pt-1 lg:pl-[4.75rem] space-y-4 max-w-3xl">
          {sec.p.map((p) => <p key={p.slice(0, 60)} className="text-[0.95rem] text-stone-600 leading-[1.8]">{p}</p>)}
          {links?.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-3">
              {links.map((l) => (
                <Link key={l.slug} to={`/p/${l.slug}`} data-testid={`info-link-${l.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 px-4 py-2 text-[11px] font-mono uppercase tracking-[0.15em] text-stone-600 hover:border-[#C85A32] hover:text-[#C85A32] transition-colors">
                  {l[lang] || l.fr} <ArrowUpRight size={12} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  </motion.section>
);

export default function InfoPage() {
  const { t, lang } = useStore();
  const { hash } = useLocation();
  const [open, setOpen] = useState("livraison");

  useEffect(() => {
    const target = hash?.replace("#", "");
    if (!target) return;
    setOpen(target);
    setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" }), 160);
  }, [hash]);

  return (
    <div className="pt-32 pb-28 px-6 lg:px-14 xl:px-24 max-w-[900px] mx-auto" data-testid="info-page">
      <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-4">NŪRA</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-stone-900">{t.info.title}</h1>
      <p className="mt-5 mb-12 text-base text-stone-600 leading-relaxed max-w-xl">{t.info.lead}</p>
      <div className="space-y-4">
        {SECTIONS.map((s) => (
          <Block
            key={s.id} id={s.id} sec={t.info[s.key]} Icon={s.Icon} links={s.links} lang={lang}
            open={open === s.id} onToggle={() => setOpen(open === s.id ? "" : s.id)}
            testId={`info-section-${s.id}`}
          />
        ))}
      </div>
      <p className="mt-12 text-xs text-stone-500 leading-relaxed">{t.info.foot}</p>
    </div>
  );
}
